// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Board → drawable geometry.
 *
 * Built once per loaded file and then drawn many times as the camera moves, so
 * every frame is a cull plus a stroke/fill of pre-built `Path2D`s rather than a
 * re-walk of the parse tree.
 *
 * Two rules from the spec shape this module:
 *
 * - §6.7: anything we cannot draw must still appear *at its position*, as a
 *   magenta hatched placeholder. Silence is the one unacceptable output, so any
 *   object that produces no geometry gets a placeholder instead of nothing.
 * - §3.5: zone fill caches are stale, but they are also the only record of the
 *   actual copper pour, so they are drawn — and the UI says they may be stale.
 */

import { atoms, get, getAll, getNums, getStr, isNode, type Node } from '../sexp/index.ts';
import { fromPoints, union, type BBox, type MaybeBBox } from '../model/bbox.ts';
import { arcExtent, arcParams, rotate, rotatedBoxExtent, type Point } from '../model/geometry.ts';
import type { Board, BoardObject } from '../model/board.ts';
import { isHiddenText, MEMBER_CONTAINER_TYPES, strokeWidth, textExtent } from '../model/objects.ts';
import { HOLE_LAYER, UNSUPPORTED_LAYER, VIA_LAYER, layerStyle } from './palette.ts';

export interface ShapeItem {
  layer: string;
  path: Path2D;
  /** Fill the path rather than stroke it. */
  filled: boolean;
  /** Stroke width in mm. 0 means a 1-pixel hairline at any zoom. */
  width: number;
  bbox: BBox;
  objectId: string;
  /**
   * Sub-order within a layer: zone pours (-1) sit under pads and track fills
   * (0), which sit under strokes and outlines (1). Without this the pour and
   * the copper on top of it would be ordered by their position in the file,
   * and a zone written last would bury the tracks it surrounds.
   */
  sub: number;
  /** Overrides the layer colour, for holes and vias. */
  color?: string;
}

export interface TextItem {
  layer: string;
  x: number;
  y: number;
  /** Degrees, KiCad convention. */
  angle: number;
  size: number;
  lines: string[];
  mirror: boolean;
  hAlign: 'left' | 'center' | 'right';
  vAlign: 'top' | 'middle' | 'bottom';
  bbox: BBox;
  objectId: string;
}

export interface DisplayList {
  shapes: ShapeItem[];
  texts: TextItem[];
  /** Layers present, in painter's order. */
  layers: string[];
  /** How many objects produced no geometry and got a placeholder. */
  placeholders: number;
}

interface Frame {
  ox: number;
  oy: number;
  rot: number;
}

const IDENTITY: Frame = { ox: 0, oy: 0, rot: 0 };

function tp(f: Frame, x: number, y: number): Point {
  if (f.rot === 0) return [x + f.ox, y + f.oy];
  const [rx, ry] = rotate(x, y, f.rot);
  return [rx + f.ox, ry + f.oy];
}

/** Accumulates paths and their extents while walking one object. */
class Builder {
  readonly shapes: ShapeItem[] = [];
  readonly texts: TextItem[] = [];
  private extent: MaybeBBox = null;
  private readonly objectId: string;

  constructor(objectId: string) {
    this.objectId = objectId;
  }

  get isEmpty(): boolean {
    return this.shapes.length === 0 && this.texts.length === 0;
  }

  shape(layer: string, path: Path2D, box: MaybeBBox, options: { filled?: boolean; width?: number; color?: string; sub?: number } = {}): void {
    if (box === null) return;
    this.shapes.push({
      layer,
      path,
      filled: options.filled ?? false,
      width: options.width ?? 0,
      bbox: box,
      objectId: this.objectId,
      sub: options.sub ?? (options.filled ? 0 : 1),
      ...(options.color !== undefined ? { color: options.color } : {}),
    });
    this.extent = union(this.extent, box);
  }

  text(item: Omit<TextItem, 'objectId'>): void {
    this.texts.push({ ...item, objectId: this.objectId });
    this.extent = union(this.extent, item.bbox);
  }
}

// --- path helpers --------------------------------------------------------------

function polyPath(pts: readonly Point[], close: boolean): { path: Path2D; box: MaybeBBox } | null {
  if (pts.length === 0) return null;
  const path = new Path2D();
  path.moveTo(pts[0]![0], pts[0]![1]);
  for (let i = 1; i < pts.length; i++) path.lineTo(pts[i]![0], pts[i]![1]);
  if (close) path.closePath();
  return { path, box: fromPoints(pts) };
}

function linePath(a: Point, b: Point): { path: Path2D; box: MaybeBBox } {
  const path = new Path2D();
  path.moveTo(a[0], a[1]);
  path.lineTo(b[0], b[1]);
  return { path, box: fromPoints([a, b]) };
}

function circlePath(cx: number, cy: number, r: number): { path: Path2D; box: BBox } {
  const path = new Path2D();
  path.arc(cx, cy, Math.max(r, 1e-6), 0, Math.PI * 2);
  return { path, box: { minX: cx - r, minY: cy - r, maxX: cx + r, maxY: cy + r } };
}

function arcPath(s: Point, m: Point, e: Point): { path: Path2D; box: BBox } {
  const params = arcParams(s, m, e);
  const path = new Path2D();
  if (params === null) {
    path.moveTo(s[0], s[1]);
    path.lineTo(e[0], e[1]);
    return { path, box: fromPoints([s, e]) as BBox };
  }
  path.arc(params.cx, params.cy, params.r, params.a0, params.a1, params.counterclockwise);
  return { path, box: arcExtent(s, m, e) };
}

/** A rounded rectangle centred at the origin, rotated, then translated. */
function roundedRectPath(
  cx: number,
  cy: number,
  w: number,
  h: number,
  radius: number,
  angleDeg: number,
): { path: Path2D; box: MaybeBBox } {
  const hw = w / 2;
  const hh = h / 2;
  const r = Math.max(0, Math.min(radius, hw, hh));
  const path = new Path2D();
  const pts: Point[] = [];

  // Walk the outline as line/arc segments in local space, transforming as we go.
  const frame: Frame = { ox: cx, oy: cy, rot: angleDeg };
  const step = (x: number, y: number): Point => {
    const p = tp(frame, x, y);
    pts.push(p);
    return p;
  };

  if (r <= 1e-9) {
    const corners: Point[] = [step(-hw, -hh), step(hw, -hh), step(hw, hh), step(-hw, hh)];
    path.moveTo(corners[0]![0], corners[0]![1]);
    for (let i = 1; i < 4; i++) path.lineTo(corners[i]![0], corners[i]![1]);
    path.closePath();
    return { path, box: fromPoints(pts) };
  }

  // Each rounded corner is a quadratic through the true corner point: exact
  // enough at any zoom a human uses, and cheaper than four transformed arcs.
  const c1 = step(-hw + r, -hh);
  const c2 = step(hw - r, -hh);
  const k1 = step(hw, -hh + r);
  const c3 = step(hw, hh - r);
  const k2 = step(hw - r, hh);
  const c4 = step(-hw + r, hh);
  const k3 = step(-hw, hh - r);
  const c5 = step(-hw, -hh + r);
  const corner1 = tp(frame, hw, -hh);
  const corner2 = tp(frame, hw, hh);
  const corner3 = tp(frame, -hw, hh);
  const corner4 = tp(frame, -hw, -hh);

  path.moveTo(c1[0], c1[1]);
  path.lineTo(c2[0], c2[1]);
  path.quadraticCurveTo(corner1[0], corner1[1], k1[0], k1[1]);
  path.lineTo(c3[0], c3[1]);
  path.quadraticCurveTo(corner2[0], corner2[1], k2[0], k2[1]);
  path.lineTo(c4[0], c4[1]);
  path.quadraticCurveTo(corner3[0], corner3[1], k3[0], k3[1]);
  path.lineTo(c5[0], c5[1]);
  path.quadraticCurveTo(corner4[0], corner4[1], c1[0], c1[1]);
  path.closePath();

  pts.push(corner1, corner2, corner3, corner4);
  return { path, box: fromPoints(pts) };
}

/**
 * A crosshair marker: a circle with a cross through it.
 *
 * Covers KiCad's `target` (a fiducial/registration mark) and KiCad 10's
 * `point`. Both are a position plus a size, and both were previously drawn as
 * magenta "cannot render this" boxes — which is a strong claim to make about a
 * marker whose entire definition is a coordinate and a diameter.
 */
function markerPaths(cx: number, cy: number, size: number, cross: 'plus' | 'x'): Array<{ path: Path2D; box: BBox }> {
  const r = Math.max(size, 0.01) / 2;
  const circle = circlePath(cx, cy, r);
  const arms = new Path2D();
  if (cross === 'x') {
    const d = r * Math.SQRT1_2;
    arms.moveTo(cx - d, cy - d);
    arms.lineTo(cx + d, cy + d);
    arms.moveTo(cx + d, cy - d);
    arms.lineTo(cx - d, cy + d);
  } else {
    arms.moveTo(cx - r, cy);
    arms.lineTo(cx + r, cy);
    arms.moveTo(cx, cy - r);
    arms.lineTo(cx, cy + r);
  }
  return [circle, { path: arms, box: circle.box }];
}

function pointsOf(n: Node, frame: Frame): Point[] {
  const holder = get(n, 'pts');
  if (!holder) return [];
  const out: Point[] = [];
  for (const xy of getAll(holder, 'xy')) {
    const v = getNums(xy, [] as string[]);
    if (v.length >= 2) out.push(tp(frame, v[0]!, v[1]!));
  }
  return out;
}

function xyOf(n: Node, token: string, frame: Frame): Point | null {
  const child = get(n, token);
  if (!child) return null;
  const v = getNums(n, token);
  return v.length >= 2 ? tp(frame, v[0]!, v[1]!) : null;
}

// --- text ------------------------------------------------------------------------

function textItemFor(n: Node, text: string, layer: string, frame: Frame): Omit<TextItem, 'objectId'> | null {
  const at = getNums(n, 'at');
  if (at.length < 2) return null;
  const [x, y] = tp(frame, at[0]!, at[1]!);
  const angle = (at[2] ?? 0) + frame.rot;

  const effects = get(n, 'effects');
  const font = effects ? get(effects, 'font') : undefined;
  const sizes = font ? getNums(font, 'size') : [];
  const size = Math.max(sizes[1] ?? sizes[0] ?? 1.27, 0.05);

  const justify = effects ? get(effects, 'justify') : undefined;
  const j = justify ? atoms(justify).map((a) => a.value) : [];

  // The real extent, not a point at the anchor: this box is what the renderer
  // culls against, and a degenerate one makes text vanish the moment its anchor
  // leaves the viewport even though the text itself is still on screen.
  const local = textExtent(n, text, false);
  const bbox = rotatedBoxExtent(local, frame.rot, frame.ox, frame.oy);

  return {
    layer,
    x,
    y,
    angle,
    size,
    lines: text.split('\n'),
    mirror: j.includes('mirror'),
    hAlign: j.includes('left') ? 'left' : j.includes('right') ? 'right' : 'center',
    vAlign: j.includes('top') ? 'top' : j.includes('bottom') ? 'bottom' : 'middle',
    bbox,
  };
}

/**
 * KiCad caches the outline polygons of TrueType text. Where that exists it is
 * the real glyph geometry and is drawn as filled shapes; everything else falls
 * back to the canvas font, which is close enough to read.
 */
function addRenderCache(b: Builder, n: Node, layer: string, frame: Frame): boolean {
  const cache = get(n, 'render_cache');
  if (cache === undefined) return false;
  let any = false;
  for (const poly of getAll(cache, 'polygon')) {
    const pts = pointsOf(poly, frame);
    const built = polyPath(pts, true);
    if (built === null) continue;
    b.shape(layer, built.path, built.box, { filled: true });
    any = true;
  }
  return any;
}

// --- pads --------------------------------------------------------------------------

function addPad(b: Builder, pad: Node, frame: Frame, board: Board): void {
  const vals = atoms(pad).map((a) => a.value);
  const shape = vals[2] ?? 'circle';
  const padType = vals[1] ?? 'smd';

  const at = getNums(pad, 'at');
  if (at.length < 2) return;
  const [cx, cy] = tp(frame, at[0]!, at[1]!);
  // The pad's stored angle is its *absolute* orientation, not one relative to
  // the footprint: a footprint at −90° writes its pads at 270°. Where a pad has
  // no angle of its own it simply follows the footprint.
  const angle = at.length >= 3 ? at[2]! : frame.rot;

  const size = getNums(pad, 'size');
  const w = size[0] ?? 0;
  const h = size[1] ?? w;

  const layers = board.layerTable.expandAll(atoms(get(pad, 'layers') ?? { kind: 'node', name: '', children: [] }).map((a) => a.value));
  const copperLayers = layers.filter((l) => l.endsWith('.Cu'));
  const drawLayers = copperLayers.length > 0 ? copperLayers : layers;

  for (const layer of drawLayers) {
    let built: { path: Path2D; box: MaybeBBox } | null = null;
    switch (shape) {
      case 'circle':
        built = circlePath(cx, cy, Math.max(w, h) / 2);
        break;
      case 'oval':
        built = roundedRectPath(cx, cy, w, h, Math.min(w, h) / 2, angle);
        break;
      case 'roundrect': {
        const ratio = getNums(pad, 'roundrect_rratio')[0] ?? 0.25;
        built = roundedRectPath(cx, cy, w, h, Math.min(w, h) * ratio, angle);
        break;
      }
      case 'trapezoid': {
        const delta = getNums(pad, 'rect_delta');
        const dx = (delta[1] ?? 0) / 2;
        const dy = (delta[0] ?? 0) / 2;
        const local: Point[] = [
          [-w / 2 - dy, -h / 2 + dx],
          [w / 2 + dy, -h / 2 - dx],
          [w / 2 - dy, h / 2 + dx],
          [-w / 2 + dy, h / 2 - dx],
        ];
        built = polyPath(local.map(([x, y]) => tp({ ox: cx, oy: cy, rot: angle }, x, y)), true);
        break;
      }
      case 'custom': {
        // Anchor shape plus the primitives drawn on top of it.
        const anchor = getStr(get(pad, 'options') ?? pad, 'anchor') ?? 'circle';
        built = anchor === 'rect' ? roundedRectPath(cx, cy, w, h, 0, angle) : circlePath(cx, cy, Math.max(w, h) / 2);
        const prims = get(pad, 'primitives');
        if (prims) addGraphics(b, prims, layer, { ox: cx, oy: cy, rot: angle }, board);
        break;
      }
      case 'rect':
      default:
        built = roundedRectPath(cx, cy, w, h, 0, angle);
        break;
    }
    if (built) b.shape(layer, built.path, built.box, { filled: true });
  }

  // Drill, drawn above copper so a plated hole reads as a hole.
  const drill = get(pad, 'drill');
  if (drill) {
    const nums = getNums(pad, 'drill');
    const oval = atoms(drill).some((a) => a.value === 'oval');
    const offset = getNums(get(drill, 'offset') ?? { kind: 'node', name: '', children: [] }, [] as string[]);
    const [hx, hy] = tp(frame, (at[0] ?? 0) + (offset[0] ?? 0), (at[1] ?? 0) + (offset[1] ?? 0));
    if (oval && nums.length >= 2) {
      const built = roundedRectPath(hx, hy, nums[0]!, nums[1]!, Math.min(nums[0]!, nums[1]!) / 2, angle);
      b.shape(HOLE_LAYER, built.path, built.box, { filled: true });
    } else if (nums.length >= 1) {
      const built = circlePath(hx, hy, nums[0]! / 2);
      b.shape(HOLE_LAYER, built.path, built.box, { filled: true });
    }
  }
  void padType;
}

// --- graphics (shared by board-level gr_* and footprint fp_*) ------------------------

/** Every graphic at any depth below `container`. */
function addNestedGraphics(b: Builder, container: Node, defaultLayer: string, frame: Frame, board: Board, depth = 0): void {
  if (depth > 6) return;
  for (const child of container.children) {
    if (!isNode(child)) continue;
    addGraphic(b, child, defaultLayer, frame, board);
    addNestedGraphics(b, child, defaultLayer, frame, board, depth + 1);
  }
}

function addGraphics(b: Builder, container: Node, defaultLayer: string, frame: Frame, board: Board): void {
  for (const child of container.children) {
    if (!isNode(child)) continue;
    addGraphic(b, child, defaultLayer, frame, board);
  }
}

function graphicLayers(n: Node, fallback: string, board: Board): string[] {
  const single = get(n, 'layer');
  if (single) {
    const names = atoms(single).map((a) => a.value);
    if (names.length > 0) return board.layerTable.expandAll(names);
  }
  const multi = get(n, 'layers');
  if (multi) {
    const names = atoms(multi).map((a) => a.value);
    if (names.length > 0) return board.layerTable.expandAll(names);
  }
  return [fallback];
}

function addGraphic(b: Builder, n: Node, fallbackLayer: string, frame: Frame, board: Board): void {
  const kind = n.name.replace(/^fp_/, 'gr_');
  const width = strokeWidth(n);
  const filled = (getStr(n, 'fill') ?? 'no') !== 'no' && getStr(n, 'fill') !== 'none';

  for (const layer of graphicLayers(n, fallbackLayer, board)) {
    switch (kind) {
      case 'gr_line': {
        const a = xyOf(n, 'start', frame);
        const c = xyOf(n, 'end', frame);
        if (!a || !c) break;
        const built = linePath(a, c);
        b.shape(layer, built.path, built.box, { width });
        break;
      }
      case 'gr_rect': {
        const a = xyOf(n, 'start', frame);
        const c = xyOf(n, 'end', frame);
        if (!a || !c) break;
        const corners: Point[] = frame.rot === 0
          ? [a, [c[0], a[1]], c, [a[0], c[1]]]
          : // A rotated frame turns the axis-aligned rect into a parallelogram;
            // rebuild it from local corners so it stays a true rectangle.
            (() => {
              const la = getNums(n, 'start');
              const lc = getNums(n, 'end');
              return [
                tp(frame, la[0]!, la[1]!),
                tp(frame, lc[0]!, la[1]!),
                tp(frame, lc[0]!, lc[1]!),
                tp(frame, la[0]!, lc[1]!),
              ];
            })();
        const built = polyPath(corners, true);
        if (built) b.shape(layer, built.path, built.box, { width, filled });
        break;
      }
      case 'gr_circle': {
        const c = xyOf(n, 'center', frame);
        const e = xyOf(n, 'end', frame);
        if (!c || !e) break;
        const built = circlePath(c[0], c[1], Math.hypot(e[0] - c[0], e[1] - c[1]));
        b.shape(layer, built.path, built.box, { width, filled });
        break;
      }
      case 'gr_arc': {
        const s = xyOf(n, 'start', frame);
        const m = xyOf(n, 'mid', frame);
        const e = xyOf(n, 'end', frame);
        if (!s || !e) break;
        const built = m ? arcPath(s, m, e) : linePath(s, e);
        b.shape(layer, built.path, built.box, { width });
        break;
      }
      case 'gr_poly': {
        const pts = pointsOf(n, frame);
        const built = polyPath(pts, true);
        if (built) b.shape(layer, built.path, built.box, { width, filled });
        break;
      }
      case 'table_cell': {
        // A table is a grid of cells, each a rectangle with text in it. Drawing
        // the cells gives both the grid and the content; the table's own
        // `border` and `separators` describe the same lines.
        const a = xyOf(n, 'start', frame);
        const c = xyOf(n, 'end', frame);
        if (!a || !c) break;
        const corners: Point[] = [a, [c[0], a[1]], c, [a[0], c[1]]];
        const built = polyPath(corners, true);
        if (built) b.shape(layer, built.path, built.box, { width });

        const text = atoms(n)[0]?.value ?? '';
        if (text === '') break;
        const font = get(get(n, 'effects') ?? n, 'font');
        const sizes = font ? getNums(font, 'size') : [];
        const size = Math.max(sizes[1] ?? sizes[0] ?? 1.27, 0.05);
        const justify = get(get(n, 'effects') ?? n, 'justify');
        const j = justify ? atoms(justify).map((x) => x.value) : [];
        const margins = getNums(n, 'margins');
        const inset = margins[0] ?? 0.5;
        const hAlign = j.includes('right') ? 'right' : j.includes('center') ? 'center' : 'left';
        const x = hAlign === 'right' ? Math.max(a[0], c[0]) - inset : hAlign === 'center' ? (a[0] + c[0]) / 2 : Math.min(a[0], c[0]) + inset;
        b.text({
          layer,
          x,
          y: (a[1] + c[1]) / 2,
          angle: frame.rot,
          size,
          lines: text.split('\n'),
          mirror: j.includes('mirror'),
          hAlign,
          vAlign: 'middle',
          bbox: fromPoints(corners) as BBox,
        });
        break;
      }
      case 'gr_bbox': {
        const a = xyOf(n, 'start', frame);
        const c = xyOf(n, 'end', frame);
        if (!a || !c) break;
        const built = polyPath([a, [c[0], a[1]], c, [a[0], c[1]]], true);
        if (built) b.shape(layer, built.path, built.box, { width });
        break;
      }
      case 'gr_vector': {
        const a = xyOf(n, 'start', frame);
        const c = xyOf(n, 'end', frame);
        if (!a || !c) break;
        const built = linePath(a, c);
        b.shape(layer, built.path, built.box, { width });
        break;
      }
      case 'point':
      case 'target': {
        const at = xyOf(n, 'at', frame);
        if (!at) break;
        const size = getNums(n, 'size')[0] ?? 1;
        const style = atoms(n).some((a2) => a2.value === 'x') ? 'x' : 'plus';
        for (const part of markerPaths(at[0], at[1], size, style)) {
          b.shape(layer, part.path, part.box, { width });
        }
        break;
      }
      case 'gr_curve': {
        const pts = pointsOf(n, frame);
        if (pts.length < 4) break;
        const path = new Path2D();
        path.moveTo(pts[0]![0], pts[0]![1]);
        path.bezierCurveTo(pts[1]![0], pts[1]![1], pts[2]![0], pts[2]![1], pts[3]![0], pts[3]![1]);
        b.shape(layer, path, fromPoints(pts), { width });
        break;
      }
      case 'gr_text':
      case 'property':
      case 'gr_text_box': {
        if (isHiddenText(n)) break;
        if (addRenderCache(b, n, layer, frame)) break;
        const vals = atoms(n).map((a) => a.value);
        const text = n.name === 'property' || n.name === 'fp_text' ? (vals[1] ?? '') : (vals[0] ?? '');
        if (text === '') break;
        const item = textItemFor(n, text, layer, frame);
        if (item) b.text(item);
        break;
      }
      default:
        break;
    }
  }
}

// --- the board -----------------------------------------------------------------------

function addFootprint(b: Builder, o: BoardObject, board: Board): void {
  const at = getNums(o.node, 'at');
  const frame: Frame = { ox: at[0] ?? 0, oy: at[1] ?? 0, rot: at[2] ?? 0 };
  const fallback = o.layers[0] ?? 'F.Cu';

  for (const child of o.node.children) {
    if (!isNode(child)) continue;
    if (child.name === 'pad') {
      addPad(b, child, frame, board);
      continue;
    }
    if (child.name === 'fp_text' || child.name === 'property' || child.name === 'fp_text_box') {
      if (isHiddenText(child)) continue;
      const layer = graphicLayers(child, fallback, board)[0] ?? fallback;
      if (addRenderCache(b, child, layer, frame)) continue;
      const vals = atoms(child).map((a) => a.value);
      const text = vals[1] ?? '';
      if (text === '') continue;
      const item = textItemFor(child, text, layer, frame);
      if (item) b.text(item);
      continue;
    }
    if (child.name.startsWith('fp_')) {
      addGraphic(b, child, fallback, frame, board);
    }
  }
}

function addZone(b: Builder, o: BoardObject, board: Board): void {
  // The computed pour: what is actually on the copper.
  for (const fill of getAll(o.node, 'filled_polygon')) {
    const layers = graphicLayers(fill, o.layers[0] ?? 'F.Cu', board);
    const pts = pointsOf(fill, IDENTITY);
    const built = polyPath(pts, true);
    if (built === null) continue;
    for (const layer of layers) b.shape(layer, built.path, built.box, { filled: true, sub: -1 });
  }
  // The outline the user drew, thin, so an unfilled zone is still visible.
  for (const poly of getAll(o.node, 'polygon')) {
    const pts = pointsOf(poly, IDENTITY);
    const built = polyPath(pts, true);
    if (built === null) continue;
    for (const layer of o.layers) b.shape(layer, built.path, built.box, { width: 0, sub: 1 });
  }
}

function addVia(b: Builder, o: BoardObject): void {
  const at = getNums(o.node, 'at');
  if (at.length < 2) return;
  const size = getNums(o.node, 'size')[0] ?? 0.6;
  const drill = getNums(o.node, 'drill')[0] ?? size / 2;
  const body = circlePath(at[0]!, at[1]!, size / 2);
  b.shape(VIA_LAYER, body.path, body.box, { filled: true });
  const hole = circlePath(at[0]!, at[1]!, drill / 2);
  b.shape(HOLE_LAYER, hole.path, hole.box, { filled: true });
}

function addTrack(b: Builder, o: BoardObject): void {
  const width = getNums(o.node, 'width')[0] ?? 0.2;
  const s = xyOf(o.node, 'start', IDENTITY);
  const e = xyOf(o.node, 'end', IDENTITY);
  if (!s || !e) return;
  const built = o.type === 'arc' ? (() => {
    const m = xyOf(o.node, 'mid', IDENTITY);
    return m ? arcPath(s, m, e) : linePath(s, e);
  })() : linePath(s, e);
  for (const layer of o.layers) b.shape(layer, built.path, built.box, { width });
}

/** Magenta hatched placeholder for anything that produced no geometry (§6.7). */
function addPlaceholder(b: Builder, o: BoardObject): void {
  if (o.bbox === null) return;
  const path = new Path2D();
  path.rect(o.bbox.minX, o.bbox.minY, o.bbox.maxX - o.bbox.minX, o.bbox.maxY - o.bbox.minY);
  b.shape(UNSUPPORTED_LAYER, path, o.bbox, { width: 0 });
}

export function buildDisplayList(board: Board): DisplayList {
  const shapes: ShapeItem[] = [];
  const texts: TextItem[] = [];
  let placeholders = 0;

  for (const o of board.objects) {
    const b = new Builder(o.id);
    switch (o.type) {
      case 'footprint':
        addFootprint(b, o, board);
        break;
      case 'zone':
        addZone(b, o, board);
        break;
      case 'via':
        addVia(b, o);
        break;
      case 'segment':
      case 'arc':
        addTrack(b, o);
        break;
      case 'group':
      case 'generated':
        // Both own other objects by UUID rather than containing geometry: a
        // tuning pattern's meander is made of ordinary track segments that are
        // drawn in their own right. Marking the container "cannot be drawn"
        // painted magenta over copper that was on screen and correct.
        break;
      case 'dimension': {
        const layer = o.layers[0] ?? 'Dwgs.User';
        const pts = pointsOf(o.node, IDENTITY);
        const built = polyPath(pts, false);
        if (built) b.shape(layer, built.path, built.box, { width: strokeWidth(o.node) });
        for (const t of getAll(o.node, 'gr_text')) addGraphic(b, t, layer, IDENTITY, board);
        break;
      }
      default: {
        const layer = o.layers[0] ?? 'Dwgs.User';
        addGraphic(b, o.node, layer, IDENTITY, board);
        // A `table` is a frame around `gr_text_box` cells, and other composite
        // tokens are the same shape: nothing at the top level, real geometry
        // one level down. Worth looking before declaring it undrawable.
        if (b.isEmpty) addNestedGraphics(b, o.node, layer, IDENTITY, board);
        break;
      }
    }

    // Containers own their geometry through other objects, so having none of
    // their own is expected rather than a rendering failure.
    if (b.isEmpty && !MEMBER_CONTAINER_TYPES.has(o.type)) {
      addPlaceholder(b, o);
      placeholders++;
    }
    shapes.push(...b.shapes);
    texts.push(...b.texts);
  }

  // Painter's order: by layer, then fills beneath strokes.
  const layers = [...new Set([...shapes.map((s) => s.layer), ...texts.map((t) => t.layer)])].sort(
    (a, b2) => layerStyle(a).order - layerStyle(b2).order || (a < b2 ? -1 : 1),
  );
  const layerRank = new Map(layers.map((l, i) => [l, i]));
  shapes.sort((x, y) => (layerRank.get(x.layer)! - layerRank.get(y.layer)!) || x.sub - y.sub);

  return { shapes, texts, layers, placeholders };
}
